import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Tag, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { api } from "@/lib/api";
import { extractError, useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";

const EMPTY = { name: "", description: "", icon: "", status: "active" };

export default function EventTypes() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage] = useState(12);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const isAdmin = user.role === "admin";

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const load = async () => {
    const params = { page, per_page: perPage };
    if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
    const { data } = await api.get("/event-types", { params });
    setItems(Array.isArray(data) ? data : data.items || []);
    setTotal(Array.isArray(data) ? data.length : data.total || 0);
    setPages(Array.isArray(data) ? 1 : data.pages || 1);
  };
  useEffect(() => { load(); }, [page, debouncedSearch]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/event-types/${editing}`, form);
        toast.success("Updated");
      } else {
        await api.post("/event-types", form);
        toast.success("Event type created");
      }
      setOpen(false); setEditing(null); setForm(EMPTY); load();
    } catch (err) { toast.error(extractError(err)); }
  };

  const del = async (id) => {
    if (!confirm("Delete this event type?")) return;
    try {
      await api.delete(`/event-types/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(extractError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="event-types-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">Configuration</div>
          <h1 className="font-serif text-4xl tracking-tighter mt-1 text-[#1A1A1A]">Event types</h1>
          <p className="text-sm text-zinc-600 mt-1">Define the kinds of events your studio handles. Custom fields attach to event types.</p>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(EMPTY); setEditing(null); } }}>
            <DialogTrigger asChild>
              <Button className="bg-[#1A1A1A] text-white hover:bg-black rounded-full" data-testid="event-type-add-btn"><Plus size={16} className="mr-1" /> New event type</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editing ? "Edit event type" : "New event type"}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3" data-testid="event-type-form">
                <div><Label>Name *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Wedding Decoration" data-testid="event-type-name-input" /></div>
                <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div className="flex items-center gap-3">
                  <Switch checked={form.status === "active"} onCheckedChange={(v) => setForm({ ...form, status: v ? "active" : "inactive" })} data-testid="event-type-status-switch" />
                  <span className="text-sm">Active</span>
                </div>
                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-[#D85C38] hover:bg-[#B84A2A] text-white" data-testid="event-type-save-btn">{editing ? "Save" : "Create"}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="relative w-full sm:w-80">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input placeholder="Search event types…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10 bg-white" />
      </div>

      {items.length === 0 ? (
        <Card className="bg-white border-black/10">
          <CardContent className="p-10 text-center text-sm text-zinc-500">
            No event types yet. Create your first to start booking events.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((et) => (
            <Card key={et.id} className="bg-white border-black/10 lift-on-hover" data-testid={`event-type-card-${et.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 flex items-center gap-1.5"><Tag size={12} /> Type</div>
                    <div className="font-serif text-xl tracking-tight mt-1">{et.name}</div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${et.status === "active" ? "bg-emerald-100 text-emerald-900" : "bg-zinc-100 text-zinc-700"}`}>{et.status}</span>
                </div>
                {et.description && <div className="text-sm text-zinc-600 mt-3 line-clamp-2">{et.description}</div>}
                <div className="mt-4 flex items-center justify-between text-xs text-zinc-500">
                  <span>{et.bookings_count} bookings</span>
                  <span>{et.custom_fields_count} custom fields</span>
                </div>
                {isAdmin && (
                  <div className="mt-4 flex gap-2 justify-end">
                    <Button variant="ghost" size="sm" onClick={() => { setForm({ name: et.name, description: et.description, icon: et.icon, status: et.status }); setEditing(et.id); setOpen(true); }} data-testid={`event-type-edit-${et.id}`}>Edit</Button>
                    <Button variant="ghost" size="sm" className="text-red-700 hover:bg-red-50" onClick={() => del(et.id)} data-testid={`event-type-delete-${et.id}`}>Delete</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="flex justify-between items-center">
        <div className="text-sm text-zinc-500">Showing {items.length ? (page - 1) * perPage + 1 : 0} - {(page - 1) * perPage + items.length} of {total}</div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            <ChevronLeft size={16} />
          </Button>
          <span className="text-sm font-medium">Page {page} of {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
}
